/**
 * Umami analytics (Option B – dynamic script).
 * Requires REACT_APP_UMAMI_URL and REACT_APP_UMAMI_WEBSITE_ID in .env.
 */

const UMAMI_URL = process.env.REACT_APP_UMAMI_URL;
const UMAMI_WEBSITE_ID = process.env.REACT_APP_UMAMI_WEBSITE_ID;

const SCRIPT_ID = 'umami-analytics-script';

function isUmamiReady() {
  return typeof window !== 'undefined' && window.umami;
}

/**
 * Injects the Umami script once if env vars are set.
 * Call once on app mount (e.g. in App.jsx useEffect).
 */
export function loadUmami() {
  if (typeof document === 'undefined' || !UMAMI_URL || !UMAMI_WEBSITE_ID) {
    return;
  }
  if (document.getElementById(SCRIPT_ID)) {
    return;
  }
  const script = document.createElement('script');
  script.id = SCRIPT_ID;
  script.defer = true;
  script.src = `${UMAMI_URL.replace(/\/$/, '')}/script.js`;
  script.setAttribute('data-website-id', UMAMI_WEBSITE_ID);
  document.body.appendChild(script);
}

/**
 * Identify the current user and account for Umami.
 * Call when user (and optionally systemSettings) is available.
 */
export function identifyUser(user, systemSettings = null) {
  if (!user || !isUmamiReady()) return;
  const userId = user.id != null ? String(user.id) : user.client_id + '-' + (user.email || 'user');
  const accountId = user.client_id != null ? String(user.client_id) : (systemSettings?.id != null ? String(systemSettings.id) : '');
  const accountName = (systemSettings?.name || '').toString().slice(0, 500);
  const data = {
    name: (user.name || '').toString().slice(0, 500),
    role: (user.role != null ? String(user.role) : '').slice(0, 500),
    account_id: accountId.slice(0, 500),
    account_name: accountName
  };
  if (user.email) data.email = String(user.email).slice(0, 500);
  if (user.phone) data.phone = String(user.phone).slice(0, 500);
  if (user.mobile) data.mobile = String(user.mobile).slice(0, 500);
  // Subscription & package info
  if (user.subscriptionType != null) data.subscription_type = String(user.subscriptionType);
  if (user.packageId != null) data.package_id = String(user.packageId);
  if (user.expire != null) data.expire = String(user.expire);
  // Account-level info from systemSettings
  if (systemSettings?.end_date) data.end_date = String(systemSettings.end_date).slice(0, 500);
  if (systemSettings?.package_id != null) data.package_id = String(systemSettings.package_id);
  if (systemSettings?.city) data.city = String(systemSettings.city).slice(0, 500);
  if (systemSettings?.country) data.country = String(systemSettings.country).slice(0, 500);
  try {
    window.umami.identify(userId, data);
  } catch (e) {
    // no-op
  }
}

/**
 * Track a session_start event with account/user context.
 * Call once per session when user is logged in (e.g. after identify).
 */
export function trackSessionStart(user, systemSettings = null) {
  if (!user || !isUmamiReady()) return;
  const accountId = user.client_id != null ? String(user.client_id) : (systemSettings?.id != null ? String(systemSettings.id) : '');
  try {
    const eventData = {
      account_id: accountId.slice(0, 500),
      account_name: (systemSettings?.name || '').toString().slice(0, 500),
      user_id: String(user.id != null ? user.id : '').slice(0, 500),
      role: (user.role != null ? String(user.role) : '').slice(0, 500)
    };
    if (user.email) eventData.email = String(user.email).slice(0, 500);
    if (user.phone) eventData.phone = String(user.phone).slice(0, 500);
    if (user.mobile) eventData.mobile = String(user.mobile).slice(0, 500);
    // Subscription & package info
    if (user.subscriptionType != null) eventData.subscription_type = String(user.subscriptionType);
    if (user.packageId != null) eventData.package_id = String(user.packageId);
    if (user.expire != null) eventData.expire = String(user.expire);
    // Account-level info from systemSettings
    if (systemSettings?.end_date) eventData.end_date = String(systemSettings.end_date).slice(0, 500);
    if (systemSettings?.package_id != null) eventData.package_id = String(systemSettings.package_id);
    if (systemSettings?.city) eventData.city = String(systemSettings.city).slice(0, 500);
    if (systemSettings?.country) eventData.country = String(systemSettings.country).slice(0, 500);
    window.umami.track('session_start', eventData);
  } catch (e) {
    // no-op
  }
}
